// src/components/BuscadorFechas.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Typography,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  FormHelperText,
  useMediaQuery,
  useTheme,
  Stack,
} from "@mui/material";
import { keyframes } from "@emotion/react";

import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import NightsStayRoundedIcon from "@mui/icons-material/NightsStayRounded";
import GroupsRoundedIcon from "@mui/icons-material/GroupsRounded";
import LoginRoundedIcon from "@mui/icons-material/LoginRounded";
import LogoutRoundedIcon from "@mui/icons-material/LogoutRounded";

import DateRangeSlider from "./DateRangeSlider";
import { useReserve } from "../context/ReserveContext";

const gradientShift = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

const isValidRange = (s, e) => {
  if (!s || !e) return false;
  const a = new Date(s + "T00:00:00");
  const b = new Date(e + "T00:00:00");
  const days = Math.floor((b - a) / (1000 * 60 * 60 * 24));
  return days >= 1;
};

export default function BuscadorFechas() {
  const {
    startDate,
    endDate,
    setStartDate,
    setEndDate,
    numGuests,
    setNumGuests,
    fetchRooms,
  } = useReserve();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [open, setOpen] = useState(false);
  const [fechasSeleccionadas, setFechasSeleccionadas] = useState(
    Boolean(startDate && endDate),
  );

  const [searchToken, setSearchToken] = useState(0);
  const [guestsTouched, setGuestsTouched] = useState(false);

  const toggleDrawer = (state) => () => setOpen(state);

  const hasDates = Boolean(startDate && endDate);
  const hasValidDates = isValidRange(startDate, endDate);
  const hasGuests = Boolean(numGuests); // "" => false

  const showGuestsError = guestsTouched && hasValidDates && !hasGuests;

  // 🎨 look & feel
  const BG_DARK = "rgba(13, 18, 32, 0.72)";
  const BG_DARK_SOLID = "rgba(13, 18, 32, 0.88)";
  const BEIGE = "rgba(229,225,208,0.92)";
  const WHITE = "rgba(255,255,255,0.92)";

  const formatDate = (iso) => {
    if (!iso) return "";
    const d = new Date(iso + "T00:00:00");
    return new Intl.DateTimeFormat("es-CL", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    }).format(d);
  };

  const nights = useMemo(() => {
    if (!hasValidDates) return 0;
    const a = new Date(startDate + "T00:00:00");
    const b = new Date(endDate + "T00:00:00");
    const diff = Math.floor((b - a) / (1000 * 60 * 60 * 24));
    return Math.max(0, diff);
  }, [startDate, endDate, hasValidDates]);

  const reserveGradientBtn = {
    color: "#fff",
    fontWeight: 800,
    textTransform: "none",
    borderRadius: 2,
    py: 1.25,
    background: "linear-gradient(270deg, #2DB193, #16a357, #c72e64)",
    backgroundSize: "600% 600%",
    animation: `${gradientShift} 3.2s ease infinite`,
    boxShadow: "0 10px 22px rgba(0,0,0,0.18)",
    transition:
      "transform 220ms ease, box-shadow 220ms ease, filter 220ms ease",
    "&:hover": {
      transform: "translateY(-1px)",
      boxShadow: "0 14px 30px rgba(0,0,0,0.22)",
      filter: "brightness(1.05)",
    },
  };

  // mientras cambian dentro del calendario
  const handleDatesChange = ({ startDate: s, endDate: e }) => {
    setStartDate(s);
    setEndDate(e);

    const a = new Date(s + "T00:00:00");
    const b = new Date(e + "T00:00:00");
    const days = Math.floor((b - a) / (1000 * 60 * 60 * 24));
    if (days >= 1) setFechasSeleccionadas(true);
  };

  // cuando el calendario auto-cierra (selección final)
  const handleDatesSelected = ({ startDate: s, endDate: e }) => {
    setFechasSeleccionadas(true);
    setStartDate(s);
    setEndDate(e);

    if (!numGuests) setGuestsTouched(true);
    setSearchToken((t) => t + 1);
  };

  // cambio huéspedes: re-buscar si ya hay fechas válidas
  const handleGuestInput = (e) => {
    const raw = e.target.value;

    setGuestsTouched(true);

    if (raw === "") {
      setNumGuests("");
      return;
    }

    const v = parseInt(raw, 10);
    const next = Number.isFinite(v) ? v : "";
    setNumGuests(next);

    if (hasValidDates) setSearchToken((t) => t + 1);
  };

  // ejecuta fetch SOLO si hay fechas válidas + huéspedes
  useEffect(() => {
    if (searchToken <= 0) return;
    if (!hasValidDates) return;

    if (!numGuests) {
      setGuestsTouched(true);
      return;
    }

    fetchRooms();
    setOpen(false);
  }, [searchToken, hasValidDates, numGuests, fetchRooms]);

  return (
    <>
      <Box
        sx={{
          position: "sticky",
          top: { xs: 72, sm: 82 },
          zIndex: 1100,
          px: { xs: 2, sm: 3, md: 6 },
          pt: 2,
        }}
      >
        <Box
          sx={{
            maxWidth: 1000,
            mx: "auto",
            p: { xs: 1.6, sm: 2 },
            borderRadius: 2,
            bgcolor: BG_DARK,
            backdropFilter: "blur(10px)",
            border: "1px solid rgba(255,255,255,0.14)",
            boxShadow: "0 14px 34px rgba(0,0,0,0.30)",
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            gap: 1.6,
            alignItems: isMobile ? "stretch" : "center",
            justifyContent: "space-between",
          }}
        >
          {/* Resumen (blanco + bold + iconos) */}
          {fechasSeleccionadas && hasValidDates ? (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.55 }}>
              <Stack
                direction="row"
                spacing={1.2}
                alignItems="center"
                flexWrap="wrap"
                sx={{ rowGap: 0.8 }}
              >
                <Stack direction="row" spacing={0.7} alignItems="center">
                  <LoginRoundedIcon sx={{ color: WHITE, fontSize: 18 }} />
                  <Typography
                    sx={{
                      color: WHITE,
                      fontWeight: 900,
                      fontSize: { xs: 16, sm: 17, md: 16, lg: 18 }, // 👈 desktop más chico
                      letterSpacing: 0.2,
                    }}
                  >
                    {formatDate(startDate)}
                  </Typography>
                </Stack>

                <Typography
                  sx={{
                    color: "rgba(255,255,255,0.50)",
                    fontWeight: 900,
                    fontSize: { xs: 16, sm: 17, md: 12, lg: 18 }, // 👈 acompaña
                    lineHeight: 1,
                  }}
                >
                  •
                </Typography>

                <Stack direction="row" spacing={0.7} alignItems="center">
                  <LogoutRoundedIcon sx={{ color: WHITE, fontSize: 18 }} />
                  <Typography
                    sx={{
                      color: WHITE,
                      fontWeight: 900,
                      fontSize: { xs: 16, sm: 17, md: 12, lg: 18 }, // 👈 idem
                      letterSpacing: 0.2,
                    }}
                  >
                    {formatDate(endDate)}
                  </Typography>
                </Stack>
              </Stack>

              <Stack
                direction="row"
                spacing={1.6}
                alignItems="center"
                flexWrap="wrap"
                sx={{ rowGap: 0.6 }}
              >
                <Stack direction="row" spacing={0.7} alignItems="center">
                  <NightsStayRoundedIcon
                    sx={{ color: "rgba(255,255,255,0.85)", fontSize: 18 }}
                  />
                  <Typography
                    sx={{
                      color: "rgba(255,255,255,0.90)",
                      fontWeight: 900,
                      fontSize: { xs: 15, sm: 16, md: 12, lg: 18 }, // 👈 desktop más chico
                      letterSpacing: 0.15,
                    }}
                  >
                    {nights} {nights === 1 ? "noche" : "noches"}
                  </Typography>
                </Stack>

                <Typography
                  sx={{
                    color: "rgba(255,255,255,0.38)",
                    fontWeight: 900,
                    fontSize: { xs: 16, sm: 17, md: 12, lg: 18 },
                    lineHeight: 1,
                  }}
                >
                  •
                </Typography>

                <Stack direction="row" spacing={0.7} alignItems="center">
                  <GroupsRoundedIcon
                    sx={{ color: "rgba(255,255,255,0.85)", fontSize: 18 }}
                  />
                  <Typography
                    sx={{
                      color: "rgba(255,255,255,0.90)",
                      fontWeight: 900,
                      fontSize: { xs: 15, sm: 16, md: 12, lg: 18 }, // 👈 desktop más chico
                      letterSpacing: 0.15,
                    }}
                  >
                    {hasGuests ? (
                      <>
                        {numGuests} {numGuests === 1 ? "huésped" : "huéspedes"}
                      </>
                    ) : (
                      <span style={{ color: "#fff" }}>
                        Seleccioná huéspedes
                      </span>
                    )}
                  </Typography>
                </Stack>
              </Stack>
            </Box>
          ) : (
            <Typography
              sx={{
                fontSize: 14,
                color: "rgba(255,255,255,0.85)",
                fontWeight: 700,
              }}
            >
              Elija fechas y cantidad de huéspedes para ver disponibilidad.
            </Typography>
          )}

          {/* Controles */}
          <Box
            sx={{
              display: "flex",
              flexDirection: isMobile ? "column" : "row",
              gap: 1.2,
              alignItems: isMobile ? "stretch" : "center",
              justifyContent: "flex-end",
              flex: 1,
            }}
          >
            {!hasDates ? (
              <Button
                variant="contained"
                onClick={toggleDrawer(true)}
                fullWidth={isMobile}
                sx={reserveGradientBtn}
              >
                Elegir fechas
              </Button>
            ) : (
              <Button
                variant="contained"
                onClick={toggleDrawer(true)}
                fullWidth={isMobile}
                startIcon={<CalendarMonthIcon />}
                sx={{
                  borderRadius: 2,
                  textTransform: "none",
                  fontWeight: 900,
                  color: "#fff",
                  borderColor: "rgba(255,255,255,0.28)",
                  py: 1.15,
                  backgroundColor: "#20AB8A",
                  "&:hover": {
                    borderColor: "rgba(255,255,255,0.42)",
                    backgroundColor: "rgba(255,255,255,0.10)",
                  },
                }}
              >
                Cambiar fechas
              </Button>
            )}

            {/* ✅ Selector Beige */}
            <FormControl
              error={showGuestsError}
              sx={{
                minWidth: 200,
                width: isMobile ? "100%" : "auto",
                "& .MuiInputLabel-root": {
                  color: showGuestsError ? "#ff6b6b" : "rgba(255,255,255,0.82)",
                  fontWeight: 900,
                  backgroundColor: BG_DARK_SOLID, // label en oscuro para que no “ensucie”
                  px: 0.7,
                  borderRadius: 1,
                },
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                  bgcolor: BEIGE,
                  "& fieldset": {
                    borderColor: showGuestsError
                      ? "#ff6b6b"
                      : "rgba(255,255,255,0.18)",
                  },
                  "&:hover fieldset": {
                    borderColor: showGuestsError
                      ? "#ff6b6b"
                      : "rgba(255,255,255,0.26)",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: showGuestsError
                      ? "#ff6b6b"
                      : "rgba(255,255,255,0.35)",
                  },
                },
                "& .MuiSelect-select": {
                  fontWeight: 900,
                  color: "#121826",
                },
                "& .MuiSvgIcon-root": {
                  color: "#121826",
                },
              }}
            >
              <InputLabel id="guests-label" shrink>
                Huéspedes*
              </InputLabel>

              <Select
                labelId="guests-label"
                id="guests"
                value={numGuests || ""}
                label="Huéspedes*"
                displayEmpty
                renderValue={(selected) => {
                  if (!selected)
                    return (
                      <span style={{ opacity: 0.65, fontWeight: 900 }}>
                        Seleccionar
                      </span>
                    );
                  return `${selected} ${
                    selected === 1 ? "persona" : "personas"
                  }`;
                }}
                onChange={handleGuestInput}
              >
                <MenuItem value="" disabled>
                  Seleccionar
                </MenuItem>
                {[...Array(10)].map((_, i) => (
                  <MenuItem key={i + 1} value={i + 1}>
                    {i + 1} {i + 1 === 1 ? "persona" : "personas"}
                  </MenuItem>
                ))}
              </Select>

              {showGuestsError && (
                <FormHelperText
                  sx={{ mt: 0.7, color: "#ff6b6b", fontWeight: 800 }}
                >
                  Seleccioná cuántos huéspedes son para continuar.
                </FormHelperText>
              )}
            </FormControl>
          </Box>
        </Box>
      </Box>

      <DateRangeSlider
        open={open}
        onClose={toggleDrawer(false)}
        startDate={startDate}
        endDate={endDate}
        onDatesChange={handleDatesChange}
        onDatesSelected={handleDatesSelected}
      />
    </>
  );
}
